import { useMemo } from 'react';

import {
  DEFAULT_SERVICE_LEVEL,
  PassengerPreferenceBC,
} from '../data/models/passenger.model';
import { useAppSelector } from './useAppSelector';
import { TransportationAttribute } from '../data/models/transportation-attribute.model';
import * as passengerSlice from '../data/passenger.slice';
import reservationUtils from '../services/reservationUtils';
import { useFeatureMemberPreferences } from './features/useFeatureMemberPreferences';

interface CareLevelMismatchOptions {
  attributeLevel1?: string;
  attributeLevel2?: string;
  mobilityAttribute?: string;
  transportationAttributes?: TransportationAttribute[];
}

export function useCareLevelMismatch(
  {
    attributeLevel1,
    attributeLevel2,
    mobilityAttribute,
    transportationAttributes,
  }: CareLevelMismatchOptions,
  deps: unknown[] = [],
): boolean {
  const memberPreferencesEnabled = useFeatureMemberPreferences();
  const passengerPreferences = useAppSelector(
    passengerSlice.selectCurrentPassengerPreferences,
  );
  const mobilityAttributes = useMemo(() => {
    if (!transportationAttributes) {
      return undefined;
    }

    return transportationAttributes.filter(
      (attribute) =>
        attribute.transportationAttributeCategoryBC ===
        reservationUtils.MobilityAttributeBC,
    );
  }, [transportationAttributes, ...deps]);

  return useMemo(() => {
    if (memberPreferencesEnabled === false) return false;
    if (!passengerPreferences) return false;

    const passengerPreference = passengerPreferences.find(
      (pp) =>
        pp.passengerProfileAttributeTypeBC ===
        PassengerPreferenceBC.serviceLevel,
    );

    const preferenceValue = (
      passengerPreference?.attributeValue?.trim() || DEFAULT_SERVICE_LEVEL
    ).toUpperCase();

    if (!transportationAttributes) return false;

    const selectedLevel1Attribute = transportationAttributes.find(
      (ta) => ta.transportationAttributeUUID === attributeLevel1,
    );
    if (!selectedLevel1Attribute?.name) return true;

    const selectedLevel2Attribute = transportationAttributes.find(
      (ta) => ta.transportationAttributeUUID === attributeLevel2,
    );

    const selectedMobilityAttribute = mobilityAttributes?.find(
      (ma) => ma.transportationAttributeUUID === mobilityAttribute,
    );
    if (!selectedMobilityAttribute?.passengerCareLevelBC) return true;

    // Passenger prefereces are currently hardcoded to C2C, D2D, and WHEELCHAIR
    // Possible combinations are:
    // Preference: WHEELCHAIR - Must have Level 2 Wheelchair and D2D attributes
    // Preference: C2C - Must have No Level 2 attribute, a level 1 Ambulatory attribute and C2C Mobility Attribute
    // Preference: D2D - Must have No Level 2 attribute, a level 1 Ambulatory attribute and D2D Mobility Attribute
    switch (preferenceValue) {
      case 'WHEELCHAIR':
        if (!selectedLevel2Attribute?.name) return true;
        const isWheelchairMatch =
          selectedLevel2Attribute.name.toUpperCase() === 'WHEELCHAIR' &&
          selectedMobilityAttribute?.passengerCareLevelBC === 'D2D';
        return !isWheelchairMatch;
      case 'C2C':
      case 'D2D':
        if (!selectedLevel1Attribute?.name) return true;
        const isMatch =
          selectedLevel1Attribute.name.toUpperCase().includes('AMBULATORY') &&
          !selectedLevel2Attribute &&
          selectedMobilityAttribute?.passengerCareLevelBC === preferenceValue;
        return !isMatch;
      default:
        return true;
    }
  }, [
    ...deps,
    attributeLevel2,
    memberPreferencesEnabled,
    mobilityAttribute,
    mobilityAttributes,
    passengerPreferences,
    transportationAttributes,
  ]);
}
